import "./navbar.scss";
import 'primeicons/primeicons.css';
import { useEffect, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { useNavigate } from "react-router-dom";
import SecurityUserResponse from "../../Models/Authentication/SecurityUserResponse";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import avatarImage from "../../Images/person_male.png";
import { Avatar } from "primereact/avatar";
import Notification from "../Notification/notification";
import { NotificationService } from "../../Services/notificationService";
import { UserService } from "../../Services/userService";
import { useLocation } from "react-router-dom";
import { useMenu } from "../../Components/Template/MenuBar/MenuContext";
import { clearStoragePreservingBrowserBranding } from "../custom-styling/applyBrowserBranding";

interface toggleSideBarProps {
    toggleSideBar: () => void;
    sideBar: boolean;
    openDialog: (open: boolean) => void;
    reloadProfile: boolean;
    setReloadProfile: React.Dispatch<React.SetStateAction<boolean>>;
}

const NavBar = ({ openDialog, reloadProfile, setReloadProfile }: toggleSideBarProps) => {
    const navigate = useNavigate();
    const [user, setUser] = useState<SecurityUserResponse>();
    const [avatar, setAvatar] = useState<string>(avatarImage);
    const { t } = useTranslation();
    const [notificationCount, setNotificationCount] = useState<number>();
    const [openNotification, setOpenNotification] = useState(false);
    const [notifications, setNotifications] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const location = useLocation();
    const userService = new UserService();
    const [gridClass, setGridClass] = useState("grid grid-nogutter");
    const [colClass, setColClass] = useState("col-fixed mr-2 my-1");
    const [open, setOpen] = useState(true)
    const notificationsLoadedRef = useRef(false);

    const { menu } = useMenu();

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1201) {
                setGridClass("grid-container grid-nogutter mr-4");
                setColClass("");
            } else {
                setGridClass("grid grid-nogutter mr-4");
                setColClass("col-fixed mr-2 my-1");
            }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const stopPropagation = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const panel = document.querySelector('.dashly-notification-panel');
            const notificationButton = document.querySelector('.navbar-notify__trigger');
            if (panel && !panel.contains(event.target as Node) && notificationButton && !notificationButton.contains(event.target as Node)) {
                setOpenNotification(false);
            }
        };

        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, []);

    useEffect(() => {
        const userAuth = secureLocalStorage.getItem('user') as string;
        if (userAuth) {
            const parsedUser = JSON.parse(userAuth);
            setUser(parsedUser);
            fetchProfilePicture(parsedUser.uuid);
        }
        fetchNotifications();
        fetchNotificationCount();

        if (reloadProfile) {
            setReloadProfile(false);
        }
    }, [reloadProfile]);

    const fetchProfilePicture = async (uuid: string) => {
        try {
            const storedAvatars = JSON.parse(localStorage.getItem("userAvatars") || "{}");
            if (storedAvatars[uuid]) {
                setAvatar(storedAvatars[uuid]);
            } else {
                const fetchedAvatar = await userService.fetchProfilePicture(uuid);
                setAvatar(fetchedAvatar);
                storedAvatars[uuid] = fetchedAvatar;
                localStorage.setItem("userAvatars", JSON.stringify(storedAvatars));
            }
        } catch (error) {
            setAvatar(avatarImage);
        }
    };

    const fetchNotifications = (options?: { silent?: boolean }) => {
        const silent = options?.silent ?? notificationsLoadedRef.current;
        if (!silent) {
            setLoading(true);
        }

        new NotificationService()
            .fetchItems()
            .then((response) => {
                const data = response.data.data;
                const fetchedNotifications = Array.isArray(data) ? data : [];
                setNotifications(fetchedNotifications);
            })
            .finally(() => {
                notificationsLoadedRef.current = true;
                if (!silent) {
                    setLoading(false);
                }
            });
    };

    const onNotificationReadChange = (uuid: string, read: boolean) => {
        setNotifications((prev) => (read ? prev.filter((n) => n.uuid !== uuid) : prev));
        setNotificationCount((count) => {
            const current = count ?? 0;
            return read ? Math.max(0, current - 1) : current + 1;
        });
    };

    const fetchNotificationCount = () => {
        new NotificationService().countNotifications().then((response) => {
            setNotificationCount(response.data as number);
        });
    };

    const handleLogout = () => {
        clearStoragePreservingBrowserBranding();
        localStorage.removeItem('userAvatars');
        navigate("/sign-in#logout", { replace: true });
    };

    useEffect(() => {
        const userAuth = secureLocalStorage.getItem('user') as string;
        if (userAuth) {
            setUser(JSON.parse(userAuth));
        }
        fetchNotifications();
        fetchNotificationCount();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchNotificationCount();
            fetchNotifications();
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        // Only close notification if it's currently open and location changes
        if (openNotification) {
            setOpenNotification(false)
        }
    }, [location])


    return (
        <div className="navbar">
            <div className="navcontent-left">
                <div className="menuicon" onClick={() => { setOpen(!open); openDialog(open) }}>
                    <i className="pi pi-bars" />
                </div>
                <div className="flex justify-content-between align-items-center w-full mr-4">
                    <div className="flex align-items-center text">
                        {menu.start ?? <h2>{t('title')}</h2>}
                    </div>
                    {menu.end && (menu.end)}
                </div>
            </div>

            <div className="navcontent-right">
                <div className="navbar-notify">
                    <button
                        type="button"
                        className="navbar-notify__trigger"
                        aria-label={t("notification.title")}
                        aria-expanded={openNotification}
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpenNotification(!openNotification);
                        }}
                    >
                        <i className="pi pi-bell" aria-hidden />
                        {notificationCount != null && notificationCount > 0 && (
                            <span className="navbar-notify__badge" aria-hidden>
                                {notificationCount > 99 ? "99+" : notificationCount}
                            </span>
                        )}
                    </button>

                    {openNotification && (
                        <>
                            {loading ? (
                                <div className="loading-spinner"></div>
                            ) : (
                                <Notification
                                    setOpenNotification={setOpenNotification}
                                    fetchNotificationCount={fetchNotificationCount}
                                    fetchNotifications={() => fetchNotifications({ silent: true })}
                                    notifications={notifications}
                                    stopPropagation={stopPropagation}
                                    onNotificationReadChange={onNotificationReadChange}
                                />
                            )}
                        </>
                    )}
                </div>

                <button
                    type="button"
                    className="profile-inline profile-inline--link"
                    onClick={() => navigate("/settings")}
                    aria-label={t("navbar.settings")}
                >
                    <div className={gridClass}>
                        <div className={colClass}>
                            <Avatar image={avatar} shape="circle" size="xlarge" icon="pi pi-user" />
                        </div>
                        <div className="col">
                            <span className="block text-left username-label">{user?.firstName ? user.firstName + ' ' + user.surname : user?.username || "-"}</span>
                        </div>
                    </div>
                </button>
                <Button text icon="pi pi-sign-out" className="logout-icon-button" onClick={handleLogout} aria-label={t("navbar.logout")} />
            </div>
        </div>
    );
};

export default NavBar;